import React, { memo } from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { Button } from "@ui/button";
import {
  Plus,
  X as CloseIcon,
  Check,
  Filter as FilterIcon,
} from "lucide-react";
import { FilterRow } from "./FilterRow";
import type { Filter } from "../useFilters";

interface Props {
  conn: AsyncDuckDBConnection | null;
  table: string;
  columns: string[];
  /** map column → declared DuckDB type (needed by FilterRow) */
  duckTypes: Record<string, string>;
  filters: Filter[];
  dirty: boolean;
  /** global combinator */
  joinOp: "AND" | "OR";
  setJoinOp: (op: "AND" | "OR") => void;
  /* CRUD helpers */
  add: () => void;
  update: (id: number, p: Partial<Filter>) => void;
  remove: (id: number) => void;
  clear: () => void;
  apply: () => void;
}

export const FiltersPanel = memo(function FiltersPanel({
  conn,
  table,
  columns,
  duckTypes,
  filters,
  dirty,
  joinOp,
  setJoinOp,
  add,
  update,
  remove,
  clear,
  apply,
}: Props) {
  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/20">
      {/* header row */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <FilterIcon className="h-4 w-4" /> Filters
        </h3>

        <div className="flex items-center gap-2">
          {/* AND / OR toggle appears once two or more filters exist */}
          {filters.length > 1 && (
            <div className="flex rounded-md overflow-hidden border text-xs font-semibold">
              {( ["AND", "OR"] as const ).map((k) => (
                <button
                  key={k}
                  className={
                    `px-2 py-0.5 focus:outline-none ${
                      joinOp === k
                        ? "bg-brand text-white"
                        : "bg-background hover:bg-muted"
                    }`
                  }
                  onClick={() => setJoinOp(k)}
                >
                  {k}
                </button>
              ))}
            </div>
          )}

          {filters.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clear} className="gap-1.5">
              <CloseIcon className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* individual rows */}
      {filters.map((f) => (
        <FilterRow
          key={f.id}
          row={f}
          allColumns={columns}
          duckTypes={duckTypes}
          conn={conn}
          table={table}
          update={update}
          remove={remove}
        />
      ))}

      {/* controls */}
      <Button variant="outline" size="sm" onClick={add} className="gap-1.5">
        <Plus className="h-4 w-4" /> Add filter
      </Button>

      <div className="pt-3">
        <Button disabled={!dirty} onClick={apply} className="gap-1.5">
          <Check className="h-4 w-4" /> Apply
        </Button>
      </div>
    </div>
  );
});